/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
const JSG = require('../../JSG');
const GraphItem = require('./GraphItem');
const BBoxShape = require('./shapes/BBoxShape');
const GraphSettings = require('./settings/GraphSettings');
const ItemAttributes = require('../attr/ItemAttributes');
const TextFormatAttributes = require('../attr/TextFormatAttributes');
const Event = require('./events/Event');
const Edge = require('./Edge');
const Path = require('./Path');
const Layer = require('./Layer');
const Port = require('./Port');
const BoundingBox = require('../../geometry/BoundingBox');
const Rectangle = require('../../geometry/Rectangle');
const Arrays = require('../../commons/Arrays');
const Dictionary = require('../../commons/Dictionary');
const FormatAttributes = require('../attr/FormatAttributes');
const Selection = require('./Selection');
const MachineContainer = require('./MachineContainer');
const GraphUtils = require('../GraphUtils');
const CellRange = require('./CellRange');
const StreamSheet = require('./StreamSheet');

/**
 * A graph is the main container for {{#crossLink "GraphItem"}}{{/crossLink}}s.</br>
 * It provides attributes to influence the global behavior.<br/>
 * As of API version 1.6.44 a <code>Graph</code> supports additional {{#crossLink
 * "GraphItem"}}{{/crossLink}}s. Additional items represents <code>GraphItem</code>s which should be
 * added to the <code>Graph</code> but not to its model. Therefore these items are managed separately and, most
 * important, are not saved or load automatically by the
 * <code>Graph</code>. Instead the object which adds these <code>GraphItem</code>s is responsible for that. To add an
 * additional <code>GraphItem</code> use {{#crossLink "Graph/addAddition:method"}}{{/crossLink}}.
 *
 * @class Graph
 * @extends GraphItem
 * @constructor
 */
class Graph extends GraphItem {
	constructor() {
		super(new BBoxShape());
		this._uniqueId = 1;
		this._graphId = 0;
		this._additions = [];
		this._layers = new Dictionary();
		this._settings = new GraphSettings(this);
		this._settings.setPortHighlightsVisible(false);
		this._settings.setGridVisible(false);
		this._settings.setScaleVisible(false);
		this._settings.setDisplayMode(0); // ENDLESS
		this._settings.setPanningEnabled(false);
		this._settings.setPortHighlightsVisible(false);


		this.getFormat().setLineStyle(FormatAttributes.LineStyle.NONE);

		this.getItemAttributes().setSelectionMode(ItemAttributes.SelectionMode.NONE);
		this.getItemAttributes().setPortMode(ItemAttributes.PortMode.NONE);
		this.getItemAttributes().setClipChildren(true);
		this.getItemAttributes().setContainer(false);

		// needed for some calculations. Set from coordinate system before draw operation
		this._zoom = 1;
		this._autoExtent = true;
		// flag whether graph has been changed and needs to be saved
		this._changed = false;
		this._isDirty = false;

		/**
		 * Maximum amount of refresh cycles. Default is <code>3</code>.
		 * @property maxRefreshCycles
		 * @type {Number}
		 * @since 2.2.20
		 */
		this.maxRefreshCycles = 3;
		this._names = [];
		this._drawEnabled = false;
		this._sheetSelection = new Selection();
		this._userSelections = new Dictionary();
		this._showViewMode = false;
		this._viewSettings = {
			showOutbox: false,
			showHeader: false,
			showGrid: false,
			allowZoom: false,
			allowScroll: false,
			showInbox: false,
		};
	}

	dispose() {
		super.dispose(this);

		this._additions.forEach((addition) => {
			addition.dispose();
			addition._parent = undefined;
		});
		Arrays.removeAll(this._additions);
		this._settings = undefined;
		// release templates which might be used within "normal" attribute lists...
		JSG.tmplStore.release();
	}

	_setShapeTo(newshape) {
		if (newshape && newshape instanceof BBoxShape) {
			super._setShapeTo(newshape);
		}
	}

	copy(deep, ids) {
		const copy = super.copy(deep, ids);
		copy._settings.setTo(this._settings);
		copy._assignItems();
		return copy;
	}

	newInstance() {
		return new Graph();
	}

	_assignName(id) {
		this.setName(`MachineGraph${id}`);
	}

	_assignItems() {
		this.subItems.forEach((item) => {
			if (item instanceof MachineContainer) {
				this._machineContainer = item;
			}
		});
	}

	getItemType() {
		return 'machinegraph';
	}

	init() {
		this._machineContainer = this.addItem(new MachineContainer());
		this._machineContainer.init();
	}

	get sheetSelection() {
		return this._sheetSelection;
	}

	set sheetSelection(selection) {
		this._sheetSelection = selection;
	}

	get machineContainer() {
		return this._machineContainer;
	}

	get streamSheetsContainer() {
		return this._machineContainer ? this._machineContainer.streamSheetsContainer : undefined;
	}

	get outboxContainer() {
		return this.machineContainer ? this.machineContainer.outboxContainer : undefined;
	}

	set activeSheetContainer(container) {
		this._activeContainer = container;
	}

	get activeSheetContainer() {
		return this._activeContainer;
	}

	setActiveSheetContainerFromGraphItem(item) {
		while (item && !(item instanceof JSG.StreamSheetContainer)) {
			item = item.getParent();
		}

		if (item) {
			this.activeSheetContainer = item;
		}
	}

	evaluate() {
		this._names.forEach((name) => {
			name.evaluate(this);
		});
		super.evaluate(this);
	}

	invalidateTerms() {
		this._names.forEach((name) => {
			name.invalidateTerm();
		});

		super.invalidateTerms();
	}

	isAddLabelAllowed() {
		return false;
	}


	/**
	 * Returns, if graph has changed.
	 *
	 * @method isChanged
	 * @return {boolean} True, if Graph has changed, otherwise false.
	 */
	isChanged() {
		return this._changed;
	}

	/**
	 * Marks graph as changed.
	 *
	 * @method setChanged
	 * @param {boolean} changed Specify <code>true</code>, if Graph has changed, otherwise false.
	 */
	setChanged(changed) {
		this._changed = changed;
	}

	setZoom(factor) {
		this._zoom = factor;
	}

	getZoom() {
		return this._zoom;
	}

	getPortFindRadius() {
		return this.getScaledZoomed(JSG.portFindRadius);
	}

	getFindRadius() {
		return this.getScaledZoomed(JSG.findRadius);
	}

	getSnapRadius() {
		return this.getScaledZoomed(JSG.snapRadius);
	}

	getCreateThreshhold() {
		return this.getScaledZoomed(JSG.createThreshhold);
	}

	/**
	 * Applies current scale and zoom settings to given value.
	 * @method getScaledZoomed
	 * @param {Number} value A value to apply current zoom and scale to
	 * @return {Number} The scaled and zoomed value.
	 * @since 2.0.20.4
	 */
	getScaledZoomed(value) {
		return this.getScaled(value) / this._zoom;
	}

	/**
	 * Applies current scale to given value.
	 * @method getScaled
	 * @param {Number} value A value to apply current scale to
	 * @return {Number} The scaled value.
	 * @since 2.0.20.5
	 */
	getScaled(value) {
		return value;
	}

	/**
	 * Marks this graph as dirty, i.e its contained models should refresh.
	 *
	 * @method markDirty
	 */
	markDirty() {
		this._isDirty = true;
	}

	/**
	 * Checks, if document is dirty due to a change.
	 *
	 * @method isDirty
	 * @return {Boolean} True, if dirty, otherwise false.
	 * @since 1.6.0
	 */
	isDirty() {
		return this._isDirty;
	}

	isRefreshNeeded() {
		return true;
	}

	/**
	 * Refreshes complete graph model until it is no longer marked as dirty or maximum refresh cycle is reached.</br>
	 * Adjust {{#crossLink "Graph/maxRefreshCycles:property"}}{{/crossLink}} to change maximum refresh
	 * cycle.
	 *
	 * @method _refreshAll
	 * @param {Boolean} [force] If set to <code>true</code> the graph model is refreshed at least once.
	 * @param {Number} cycle Current refresh cycle.
	 * @return {Boolean} <code>true</code> if model was refreshed or <code>false</code> if not.
	 * @private
	 * @since 2.2.20
	 */
	_refreshAll(force, cycle) {
		const refresh = this._isDirty === true || force === true;
		if (refresh) {
			this._isDirty = false;
			super._doRefresh(force);
			// still dirty? => refresh again...
			if (this._isDirty && cycle < this.maxRefreshCycles) {
				this._refreshAll(force, cycle + 1);
			}
		}
		return refresh;
	}

	_doRefresh(force) {
		// console.log('refresh');

		if (this._refreshAll(force, 1)) {
			// console.log("in");
			this.__timeRefresh = undefined;
			this.getSettings().refresh();
			this.layoutAll();
			this.updateExtent();
			const event = new Event(Event.GRAPH, this);
			event.detailId = Graph.AttributeID.REFRESH;
			event.source = this;
			this.sendPostEvent(event);
			this.setRefreshNeeded(false);
		}
	}

	// overwritten to call our BridgeBuilder if it exists...
	layoutAll() {
		super.layoutAll();
	}

	/**
	 * Returns the layer for given tag.</br>
	 * If no layer is known for given name, a new one will be created and registered.
	 *
	 * @method getLayer
	 * @param {String} name A unique layer name.
	 * @return {Layer} The layer registered for given name.
	 */
	getLayer(name) {
		let layer = this._layers.get(name);
		if (layer === undefined && name !== undefined) {
			layer = this._layers.put(name, new Layer(name));
		}

		return layer;
	}

	/**
	 * Remove a layer.</br>
	 *
	 * @method removeLayer
	 * @param {String} name A unique layer name.
	 * @since 3.0
	 */
	removeLayer(name) {
		this._layers.remove(name);
	}

	/**
	 * Checks, if a layer exists.</br>
	 *
	 * @method hasLayer
	 * @param {String} name A unique layer name.
	 * @return {boolean} True, if layer exists, otherwise false.
	 */
	hasLayer(name) {
		const layer = this._layers.get(name);

		return layer !== undefined;
	}

	getSettings() {
		return this._settings;
	}

	setSettings(settings) {
		if (settings !== undefined) {
			this._settings.deregister(this);
			this._settings = settings;
			this._settings.register(this);
		}
	}

	saveContent(file, absolute) {
		file.writeAttributeString('uniqueid', this._uniqueId);

		this._settings.save(file);

		file.writeStartElement('layers');
		file.writeStartArray('layer');

		const layerArray = this._layers.elements();

		layerArray.forEach((layer) => {
			layer.save(file);
		});

		file.writeEndArray('layer');
		file.writeEndElement();

		super.saveContent(file, absolute);

		this._changed = false;
	}

	// overwritten to support async read:
	read(reader, object, opts) {
		super.read(reader, object, opts);
		if (!opts || !opts.async) {
			this._postRead(reader, object);
		}
	}

	_postRead(reader, object) {
		reader.iterateObjects(object, (name, child) => {
			switch (name) {
				case 'layer': {
					const layer = new Layer(reader.getAttribute(child, 'name'));
					layer.read(reader, child);
					this._layers.put(layer.name, layer);
					break;
				}
				case 'settings':
					this._settings.read(reader, child);
					break;
				default:
					break;
			}
		});

		// restore unique id
		const id = reader.getAttribute(object, 'uniqueid');
		if (id !== undefined) {
			this._uniqueId = Number(id);
		}

		this._restoreConnections(this);
		this.invalidateTerms();
		this.evaluate();
		this.refresh();

		this._changed = false;
	}

	createPath() {
		// currently a graph has no id, so we simply return a fresh Path without an id...
		return new Path();
	}

	/**
	 * Convenience method to find an arbitrary port by its id.</br>
	 * This will run through all {{#crossLink "Node"}}{{/crossLink}}s within this
	 * graph.
	 *
	 * @method getPortById
	 * @param {Number} id The id of the port to look for.
	 * @return {Port} The corresponding port or <code>undefined</code> if none could be found.
	 */
	getPortById(id) {
		const item = this.getItemById(id);
		return item && item instanceof Port ? item : undefined;
	}

	getItemById(id) {
		let i;
		let n;
		let item;

		for (i = 0, n = this._subItems.length; i < n; i += 1) {
			item = this._subItems[i].getItemById(id);
			if (item !== undefined) {
				return item;
			}
		}

		return undefined;
	}

	getItemByName(name) {
		let i;
		let n;
		let item;

		for (i = 0, n = this._subItems.length; i < n; i += 1) {
			item = this._subItems[i].getItemByName(name);
			if (item !== undefined) {
				return item;
			}
		}

		return undefined;
	}

	translateFromParent(point) {
		// do nothing intentionally
		return point;
	}

	translateToParent(point) {
		// do nothing intentionally
		return point;
	}

	/**
	 * Set to define, whether graph automatically updates the graph extent, i.e. updates the {{#crossLink
	 * "BoundingBox"}}{{/crossLink}} of this graph to cover all items.
	 *
	 * @method setAutoExtent
	 * @param {boolean} flag True, if graph should automatically derive its size from the contained items.
	 * @since 2.0.0
	 */
	setAutoExtent(flag) {
		this._autoExtent = flag;
	}

	/**
	 * Returns, whether graph automatically updates the graph extent, i.e. updates the {{#crossLink
	 * "BoundingBox"}}{{/crossLink}} of this graph to cover all items.
	 *
	 * @method getAutoExtent
	 * @return {boolean} True, if graph should automatically derive its size from the contained items.
	 * @since 2.0.0
	 */
	getAutoExtent() {
		return this._autoExtent;
	}

	/**
	 * Updates the graph extent, i.e. updates the {{#crossLink "BoundingBox"}}{{/crossLink}}
	 * of this graph to cover all items.
	 *
	 * @method updateExtent
	 */
	updateExtent() {
		if (this._reading || JSG.idUpdater.isActive) {
			// to avoid unnecessary recalculations
			return;
		}
		if (this._autoExtent) {
			const totalbox = JSG.boxCache.get();
			const totalrect = JSG.rectCache.get();
			this.getTotalBoundingRect(undefined, totalrect);
			this.setBoundingBoxTo(totalrect.toBoundingBox(totalbox));
			JSG.boxCache.release(totalbox);
			JSG.rectCache.release(totalrect);
		}
	}

	getTotalBoundingRect(target, reuserect) {
		const totalrect = reuserect || new Rectangle(0, 0, 0, 0);
		const tmprect = JSG.rectCache.get();

		this._subItems.forEach((subItem) => {
			const subrect = subItem.getTotalBoundingRect(this, tmprect);
			totalrect.union(subrect);
		});
		JSG.rectCache.release(tmprect);
		return totalrect;
	}

	/**
	 * Returns the surrounding rectangle, which is used by contained GraphItems
	 *
	 * @method getUsedRect
	 * @param {Rectangle} [reuserect] An optional rectangle to reuse.
	 * @return {Rectangle} Used rect.
	 */
	getUsedRect(reuserect) {
		const targetRect = reuserect || new Rectangle(0, 0, 0, 0);
		if (this.getItemCount()) {
			const box = JSG.boxCache.get();
			this.getItemAt(0)
				.getTranslatedBoundingBox(this, box)
				.getBoundingRectangle(targetRect);
			JSG.boxCache.release(box);
		}
		return this.getTotalBoundingRect(undefined, targetRect);
	}

	/**
	 * Returns the graph identifier.
	 *
	 * @method getGraphId
	 * @return {Number} The graph identifier
	 */
	getGraphId() {
		return this._graphId;
	}

	/**
	 * Sets the graph id.</br>
	 * <b>Note:</b> use with care. Usually it is not required to set or change the graph id manually.
	 *
	 * @method setGraphId
	 * @param {Number} id The new graph id.
	 */
	setGraphId(id) {
		this._graphId = id;
	}

	getGraph() {
		return this;
	}

	get graph() {
		return this;
	}

	// overwritten to simply return corresponding template because a graph has no TextFormat...
	getTextFormat() {
		const template = JSG.tmplStore.getTemplate(TextFormatAttributes.TemplateID);
		return template || super.getTextFormat();
	}

	// overwritten since label placement is not useful for a graph...
	updateLabelPositions() {}

	setProtected(protect) {
		JSG.streamSheets.forEach(container => {
			container.sheet.protected = protect;
		})
	}

	getStreamSheetContainerById(id) {
		const container = this.streamSheetsContainer;
		if (container === undefined) {
			return undefined;
		}
		let result;

		container.enumerateStreamSheetContainers((sheet) => {
			if (id === sheet.properties.sheetId) {
				result = sheet;
			}
		});

		return result;
	}

	getSheetSelection() {
		const container = this.streamSheetsContainer;
		let result;

		container.enumerateStreamSheetContainers((sheet) => {
			if (
				sheet
					.getStreamSheet()
					.getOwnSelection()
					.hasSelection()
			) {
				result = sheet.getStreamSheet().getOwnSelection();
			}
		});

		return result;
	}

	arrange(width, height) {
		if (this._machineContainer === undefined) {
			return;
		}

		const box = JSG.boxCache.get();

		box.setLeft(0);
		box.setTop(0);
		box.setWidth(width);
		box.setHeight(height);

		this._machineContainer.setBoundingBoxTo(box);

		JSG.boxCache.release(box);
	}

	getUniqueName(base, id) {
		let name;
		let cnt = 1;
		let found = false;

		const search = (item) => {
			name = `${base}${cnt}`;
			if (name === item.getName().getValue()) {
				found = true;
				cnt += 1;
			}
		};

		do {
			found = false;
			GraphUtils.traverseItem(this, (item) => search(item));
		} while (found);

		const getShapeByName = (newName) => {
			return JSG.streamSheets.find((sheetContainerModel) => {
				const { sheet } = sheetContainerModel;
				return sheet.shapes.shapes.find((shape) => shape.name && shape.name.v === newName);
			});
		};

		while (getShapeByName(name)) {
			cnt += 1;
			name = `${base}${cnt}`;

		}

		return name;
	}

	clearSearchResult() {
		this.searchResult = undefined;
		this.activeSearchIndex = -1;
	}

	collectSearchResult(key, options) {
		const cont = this.streamSheetsContainer;

		this.searchResult = undefined;

		if (key === '') {
			return;
		}

		if (!options.matchCase) {
			key = key.toUpperCase();
		}

		JSG.streamSheets.forEach(sheetContainerModel => {
			const sheet = sheetContainerModel.sheet;
			if (!options.activeSheet || sheet === options.sheet) {
				const data = sheet.cells;
				// const shift = sheet.model.colOffset

				data.enumerate((column, row, cell) => {
					let val = cell.getValue();
					if (val !== undefined) {
						val = String(val);
						if (!options.matchCase) {
							val = val.toUpperCase();
						}
						if (val.indexOf(key) !== -1) {
							if (!this.searchResult) {
								this.searchResult = [];
							}
							this.searchResult.push(new CellRange(sheet, column, row));
						} else if (options.formulas) {
							let formula = cell.getFormula();
							if (formula) {
								if (!options.matchCase) {
									formula = formula.toUpperCase();
								}
								if (formula.indexOf(key) !== -1) {
									if (!this.searchResult) {
										this.searchResult = [];
									}
									this.searchResult.push(new CellRange(sheet, column, row));
								}
							}
						}
					}
				});
			}
		});

		this.activeSearchIndex = -1
		this.getNextSearchResult();
	}

	getSheetIndex(sheet) {
		return JSG.streamSheets.findIndex(streamSheetModel => streamSheetModel.sheet === sheet);
	}

	getNextSearchResult() {
		if (!this.searchResult) {
			return;
		}

		const selection = this.getSheetSelection();

		if (!selection) {
			return;
		}

		if (this.activeSearchIndex === -1) {
			this.activeSearchIndex = 0;
			for (let i = 0; i < this.searchResult.length; i += 1) {
				const range = this.searchResult[i];
				// cell behind in this sheet
				if (range.sheet === selection.sheet &&
					((range.getX1() >= selection.getActiveCell().x && range.getY1() === selection.getActiveCell().y) ||
						range.getY1() > selection.getActiveCell().y)) {
					this.activeSearchIndex = i;
					break;
				}
				// next sheet
				if (this.getSheetIndex(range.sheet) > this.getSheetIndex(selection.sheet)) {
					this.activeSearchIndex = i;
					break;
				}
			}
		} else if (this.activeSearchIndex === this.searchResult.length - 1) {
			this.activeSearchIndex = 0;
		} else {
			this.activeSearchIndex += 1;
		}
	}

	getPreviousSearchResult() {
		if (this.activeSearchIndex > 0) {
			this.activeSearchIndex -= 1;
		} else {
			this.activeSearchIndex = this.searchResult.length - 1;
		}
	}

	resolveCustomReference(item, property) {
		let sheet = item;

		while (sheet && !(sheet instanceof StreamSheet)) {
			sheet = sheet.getParent();
		}

		return sheet ? sheet.getCustomReference(property) : undefined;
	}

	set viewSettings(mode) {
		this._viewSettings = mode;
	}

	get viewSettings() {
		return this._viewSettings || {};
	}

	set showViewMode(mode) {
		this._showViewMode = mode;
	}

	get showViewMode() {
		return this._showViewMode;
	}

	/**
	 * ID definitions used within event handling. Each id is of type <code>String</code> and used as
	 * the detailId field of {{#crossLink "Event"}}{{/crossLink}}.
	 *
	 * @property AttributeID
	 * @type Object
	 * @static
	 */
	static get AttributeID() {
		return {
			/**
			 * @property REFRESH
			 * @type String
			 * @static
			 */
			REFRESH: 'refresh'
		};
	}
}

module.exports = Graph;
